import React from 'react'
import { Box, Button, Field, Message, Modal, Textarea } from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEditQuestionFlowContextValue } from '../context/EditQuestionFlowProvider'
import { useRequirementDetailWorkflowsContextValue } from '../../../context/RequirementDetailWorkflowProvider'
import { editQuestionSchema, EditQuestionSchema } from '../schema'

export const EditQuestionFlowMain = () => {
  const {
    editQuestionFlow: { isOpen, close },
  } = useRequirementDetailWorkflowsContextValue()

  const {
    state: {
      question,
      mutationState: { isPending, isError, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useEditQuestionFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<EditQuestionSchema>({
    resolver: zodResolver(editQuestionSchema),
    values: {
      question: question.question,
      idealAnswer: question.idealAnswer,
      evaluationCriteria: question.evaluationCriteria,
    },
  })

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        questionId: question.id,
        ...data,
      },
      {
        onSuccess: () => {
          handleClose()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      title="Edit Question on Requirement"
      loading={isPending}
      showCancel
      customCta={
        <Button variant="primary" loading={isPending} onClick={onSubmit}>
          Save
        </Button>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <Field label="Question" error={errors.question?.message?.toString()}>
          <Textarea
            {...register('question')}
            aria-label="Question"
            minRows={3}
          />
        </Field>

        <Field
          label="Ideal Answer"
          error={errors.idealAnswer?.message?.toString()}
        >
          <Textarea
            {...register('idealAnswer')}
            aria-label="Ideal Answer"
            minRows={3}
          />
        </Field>

        <Field
          label="Evaluation Criteria"
          error={errors.evaluationCriteria?.message?.toString()}
        >
          <Textarea
            {...register('evaluationCriteria')}
            aria-label="Evaluation Criteria"
            minRows={3}
          />
        </Field>

        {isError && errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
